import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { ConceptType, PermitType } from "@ha/data/basic";
import { HaModalService } from "@ha/ui/common";
import { BaseModalComponent } from "@ha/ui/common";
import { PwaState } from "@ha/util/pwa";
import { TranslocoModule } from "@ngneat/transloco";
import { Navigate } from "@ngxs/router-plugin";
import { Actions, Select, Store } from "@ngxs/store";
import { Observable } from "rxjs";
import { PermitCreateActions } from "../../../permit-create/states/permit-create.actions";
import { PermitQuestionActions } from "../../../permit-create/states/permit-question.actions";
import { PermitPaths } from "../../../permit-paths";
import { PermitStepActions } from "../../../permit/states/permit-step.action";
import { PermitPerson } from "../../../shared/models/permit-person.model";
import { PermitUserSelectors } from "../../../shared/states/permit-user.selectors";
import { SubscriptionsSelectors } from "../../../subscriptions/states/subscriptions.selectors";
import { PermitReturnDelegationModalComponent } from "../permit-return-delegation-modal/permit-return-delegation-modal.component";
import { PremiumInfoModalComponent } from "../premium-info-modal/premium-info-modal.component";

export enum PermitCreateMode {
    Premium = "premium",
    Base = "base",
    Demo = "demo",
    Superintendent = "superintendent",
}

export interface PermitCreateModalData {
    mode: PermitCreateMode;
}

@Component({
    selector: "pmt-permit-create-modal",
    templateUrl: "./permit-create-modal.component.html",
    styleUrls: ["./permit-create-modal.component.scss"],
    standalone: true,
    imports: [CommonModule, TranslocoModule],
})
export class PermitCreateModalComponent extends BaseModalComponent implements OnInit {
    public mode: PermitCreateMode = PermitCreateMode.Base;
    public PermitCreateMode = PermitCreateMode;
    public PermitPaths = PermitPaths;
    public ConceptType = ConceptType;

    @Select(PermitUserSelectors.currentPerson)
    public currentPerson$: Observable<PermitPerson>;

    @Select(PermitUserSelectors.hasValidCertificate)
    public hasValidCertificate$!: Observable<boolean>;

    @Select(SubscriptionsSelectors.hasDelegationsToReturn)
    public hasDelegationsToReturn$!: Observable<boolean>;

    public PermitType = PermitType;

    constructor(private store: Store, private modalService: HaModalService, private actions: Actions) {
        super();
    }

    public ngOnInit(): void {
        if (this.data && typeof this.data === "object" && "mode" in this.data) {
            this.mode = (this.data as PermitCreateModalData).mode;
        }
    }

    public onCreatePermit(event: MouseEvent, isPremium: boolean, permitType: PermitType = PermitType.hotWork): void {
        event.stopPropagation();
        this.store.dispatch(new PermitStepActions.StartCreate(isPremium, false));
        this.store.dispatch(new PermitCreateActions.CreatePermit(isPremium, false, permitType));
        this.store.dispatch(new PermitQuestionActions.ResetToDefault());
    }

    public onPreparePermit(event: MouseEvent): void {
        event.stopPropagation();
        this.store.dispatch(new Navigate([PermitPaths.Root, PermitPaths.Prepare]));
        this.closeModal();
    }

    public onGetPreparedPermit(event: MouseEvent): void {
        event.stopPropagation();
        this.store.dispatch(new Navigate([PermitPaths.Root, PermitPaths.PreparedPermits]));
        this.closeModal();
    }

    public onReturnDelegatedPermit(event: MouseEvent): void {
        event.stopPropagation();
        this.closeModal();
        this.modalService.open(PermitReturnDelegationModalComponent, true, {}, "header");
    }

    public onCreateDemo(
        event: MouseEvent,
        isPremium: boolean,
        permitType: PermitType = PermitType.hotWork,
    ): void {
        event.stopPropagation();
        this.store.dispatch(new PermitStepActions.StartCreate(isPremium, true));
        this.store.dispatch(new PermitCreateActions.CreatePermit(isPremium, true, permitType));
        this.store.dispatch(new PermitQuestionActions.ResetToDefault());
        this.closeModal();
    }

    public openPremiumInfo(event: MouseEvent): void {
        this.closeModal();
        event.stopPropagation();
        this.modalService.open(PremiumInfoModalComponent, true, {}, "header");
    }

    public closeIfMobile(): void {
        const isMobile = this.store.selectSnapshot(PwaState.isMobile);
        if (isMobile) {
            this.closeModal();
        }
    }
}
